<template>
  <Transition enter-active-class="animate-enter" leave-active-class="animate-leave" appear>
    <div class="input-field">
      <label :for="id" class="input-label">{{ label }}</label>
      <textarea v-if="textarea" :id="id" :value="modelValue"
        @input="$emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)" :required="required"
        :placeholder="placeholder" class="input-control textarea" :disabled="!editable" v-bind="$attrs"></textarea>
      <input v-else :id="id" :value="modelValue"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)" :type="type" :required="required"
        :placeholder="placeholder" class="input-control" :disabled="!editable" v-bind="$attrs" />
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { computed } from 'vue'

  const props = defineProps({
    label: {
      type: String,
      required: true
    },
    modelValue: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    required: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    textarea: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    }
  })

  defineEmits(['update:modelValue'])

  const id = computed(() => {
    return `input-${props.label.toLowerCase().replace(/\s+/g, '-')}`
  })
</script>

<style scoped>
  .input-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .input-label {
    font-weight: 500;
    color: var(--p-background-600);
    font-size: 0.875rem;
  }

  /* Colours come from the PrimeVue preset (mytheme.ts) so the field follows
     the portal's light/dark mode like the PrimeVue inputs do. */
  .input-control {
    padding: 0.625rem;
    border: 1px solid var(--p-background-300);
    border-radius: 0.375rem;
    font-size: 0.875rem;
    width: 100%;
    background-color: var(--p-background-0);
    color: var(--p-text-color);
    transition:
      border-color 0.2s,
      box-shadow 0.2s;
    font-family: inherit;
  }

  .input-control:hover:not(:disabled) {
    border-color: var(--p-background-400);
  }

  .input-control:focus:not(:disabled) {
    outline: none;
    border-color: var(--p-primary-500);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-primary-500) 20%, transparent);
  }

  .input-control::placeholder {
    color: var(--p-text-muted-color);
  }

  .input-control:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background-color: var(--p-background-100);
  }

  .textarea {
    min-height: 100px;
    resize: vertical;
  }

  /* Enter and leave animations */
  .animate-enter {
    animation: fadeIn 0.3s ease-out;
  }

  .animate-leave {
    animation: fadeOut 0.3s ease-in;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }

    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
</style>
